"use client"

import * as React from "react"

import { cn } from "@ibada/ui"

import { ReceiptTemplateKey } from "@/features/finance/model/finance-types"

type PaperSize = { width: number; height: number }

/**
 * The paper each template is drawn on, in CSS pixels at 96 dpi (210 x 297 mm, or an 80 mm roll). Only the first
 * guess: once the document has loaded, the real size is read from its `.receipt` element, so a caller that
 * does not know the template (an issued receipt) still gets the right paper.
 */
const PAPER_SIZE: Record<ReceiptTemplateKey, PaperSize> = {
  [ReceiptTemplateKey.Classic]: { width: 794, height: 1123 },
  [ReceiptTemplateKey.Compact]: { width: 303, height: 720 },
  [ReceiptTemplateKey.Formal]: { width: 794, height: 1123 },
}

export function receiptPaperSize(templateKey: ReceiptTemplateKey | null | undefined): PaperSize {
  return (templateKey && PAPER_SIZE[templateKey]) || PAPER_SIZE[ReceiptTemplateKey.Classic]
}

/**
 * The scale that shows the WHOLE page inside the available box: min(width ratio, height ratio, 1). Never
 * enlarges past real size. Zero until both the box and the paper have been measured.
 */
export function fitReceiptScale(available: PaperSize, paper: PaperSize): number {
  if (available.width <= 0 || available.height <= 0 || paper.width <= 0 || paper.height <= 0) return 0
  return Math.min(available.width / paper.width, available.height / paper.height, 1)
}

/**
 * Reads the paper's real size from the rendered document. Null until the receipt itself is there (the frame
 * starts as a blank page whose height is just the frame's own), so the first guess stands until then.
 */
function measurePaper(frame: HTMLIFrameElement | null, fallback: PaperSize): PaperSize | null {
  const doc = frame?.contentDocument
  const paper = doc?.querySelector<HTMLElement>(".receipt")
  if (!doc || !paper) return null
  const rect = paper.getBoundingClientRect()
  const width = Math.ceil(rect.width) || fallback.width
  const height = Math.ceil(rect.bottom + (doc.defaultView?.scrollY ?? 0)) || fallback.height
  return { width, height }
}

type ReceiptPaperFitProps = {
  /** The receipt document, as the server renders it. */
  html: string
  title: string
  /** A hint for the first frame before the document is measured. */
  templateKey?: ReceiptTemplateKey | null
  /**
   * A fixed zoom (1 = real size). The only case the paper may be larger than the box: the box then scrolls.
   * Absent: fit the whole page.
   */
  zoom?: number | null
  /** Space kept between the paper and the box edge, on every side, in px. */
  padding?: number
  /** Shown in the box until there is a document. */
  placeholder?: React.ReactNode
  /** The box. It must get its height from the caller (a fixed or flex height): the paper fits inside it. */
  className?: string
  /** For printing the preview (`contentWindow.print()`). */
  frameRef?: React.Ref<HTMLIFrameElement>
  /** Fires with the scale actually applied, so a zoom control can start from it. */
  onScaleChange?: (scale: number) => void
}

/**
 * The one way a receipt is shown on screen: the document at its natural paper size, scaled down to fit the box
 * it is given, so nothing is clipped and no scrollbar appears. Re-fits whenever the box changes size (window
 * resize, a dialog opening, a sidebar collapsing). The frame is a picture of the paper: not focusable and not
 * interactive.
 */
export function ReceiptPaperFit({
  html,
  title,
  templateKey,
  zoom,
  padding = 16,
  placeholder,
  className,
  frameRef,
  onScaleChange,
}: ReceiptPaperFitProps) {
  const boxRef = React.useRef<HTMLDivElement | null>(null)
  const innerFrameRef = React.useRef<HTMLIFrameElement | null>(null)
  const [box, setBox] = React.useState<PaperSize>({ width: 0, height: 0 })
  const [paper, setPaper] = React.useState<PaperSize>(() => receiptPaperSize(templateKey))

  const setFrame = React.useCallback(
    (node: HTMLIFrameElement | null) => {
      innerFrameRef.current = node
      if (typeof frameRef === "function") frameRef(node)
      else if (frameRef) (frameRef as React.MutableRefObject<HTMLIFrameElement | null>).current = node
    },
    [frameRef],
  )

  React.useLayoutEffect(() => {
    const element = boxRef.current
    if (!element) return
    const update = () => {
      const next = { width: element.clientWidth, height: element.clientHeight }
      setBox((prev) => (prev.width === next.width && prev.height === next.height ? prev : next))
    }
    update()
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update)
    observer?.observe(element)
    window.addEventListener("resize", update)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  const remeasure = React.useCallback(() => {
    const next = measurePaper(innerFrameRef.current, receiptPaperSize(templateKey))
    if (next) setPaper((prev) => (prev.width === next.width && prev.height === next.height ? prev : next))
  }, [templateKey])

  // A narrower paper reflows the document, so its height is read again once the frame has the new width.
  React.useLayoutEffect(() => {
    remeasure()
  }, [paper.width, remeasure])

  const available = { width: box.width - padding * 2, height: box.height - padding * 2 }
  const scale = zoom && zoom > 0 ? zoom : fitReceiptScale(available, paper)

  React.useEffect(() => {
    if (scale > 0) onScaleChange?.(scale)
  }, [onScaleChange, scale])

  const scaledWidth = paper.width * scale
  const scaledHeight = paper.height * scale

  return (
    <div
      ref={boxRef}
      data-testid='receipt-paper-box'
      className={cn("bg-muted relative min-h-0 min-w-0", zoom ? "overflow-auto" : "overflow-hidden", className)}
      style={{ padding }}
    >
      {html ? (
        <div
          data-testid='receipt-paper'
          data-scale={scale.toFixed(4)}
          className='relative mx-auto overflow-hidden rounded-[3px] bg-white shadow-sm ring-1 ring-black/5'
          style={{ width: scaledWidth, height: scaledHeight }}
        >
          <iframe
            ref={setFrame}
            title={title}
            srcDoc={html}
            onLoad={remeasure}
            tabIndex={-1}
            scrolling='no'
            className='pointer-events-none absolute top-0 left-1/2 block border-0 bg-white'
            style={{
              width: paper.width,
              height: paper.height,
              transform: `translateX(-50%) scale(${scale})`,
              transformOrigin: "top center",
            }}
          />
        </div>
      ) : (
        <div className='text-muted-foreground flex h-full items-center justify-center p-4 text-center text-xs font-semibold'>{placeholder}</div>
      )}
    </div>
  )
}
